import { entries } from '@shared/objects';
import { toEnumValue } from '@shared/enums';
import { CurrencyCode, WeightUnit } from './intl';

// Reference
// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/Locale

export enum LocaleId {
  EnglishUnitedStates = 'en-US',
  EnglishGreatBritain = 'en-GB',
  EnglishIreland = 'en-IE',
  JapaneseJapan = 'ja-JP',
}

export interface LocaleProps {
  currencyCode: CurrencyCode;
  weightUnit: WeightUnit;
}

export interface Locale extends LocaleProps {
  id: LocaleId;
}

export const localesMap: { [key in LocaleId]: LocaleProps } = {
  [LocaleId.EnglishUnitedStates]: { currencyCode: CurrencyCode.UnitedStatesDollar, weightUnit: WeightUnit.Ounce },
  [LocaleId.EnglishGreatBritain]: { currencyCode: CurrencyCode.PoundSterling, weightUnit: WeightUnit.Kilogram },
  [LocaleId.EnglishIreland]: { currencyCode: CurrencyCode.Euro, weightUnit: WeightUnit.Kilogram },
  [LocaleId.JapaneseJapan]: { currencyCode: CurrencyCode.JapaneseYen, weightUnit: WeightUnit.Gram },
};

export const locales = entries(localesMap).map((each) => {
  const locale: Locale = { id: each[0], ...each[1] };
  return locale;
});

export function getLocale(id: LocaleId) {
  const locale = locales.find((locale) => locale.id === id);
  if (!locale) {
    throw new Error(`The locale with id '${id}' was not found.`);
  }
  return locale;
}

export function newIntlLocale(id: LocaleId) {
  const locale = getLocale(id);
  return new Intl.Locale(`${locale.id}`);
}

/** Default locale used when auto-detection fails or runs on the server */
export const DEFAULT_LOCALE_ID = LocaleId.EnglishUnitedStates;

/**
 * Detect the user's locale from the browser's navigator.language.
 * Tries exact match first (e.g., "en-US"), then language-only match (e.g., "en" → "en-US").
 * Falls back to DEFAULT_LOCALE_ID if no match is found.
 * Returns undefined on the server (no navigator).
 */
export function detectLocale(): Locale {
  if (typeof navigator === 'undefined') return getLocale(DEFAULT_LOCALE_ID);

  const browserLang = navigator.language;

  // Exact match (e.g., "en-US" → LocaleId.EnglishUnitedStates)
  const exact = toEnumValue(LocaleId, browserLang, { ignoreCase: true });
  if (exact) return getLocale(exact);

  // Language-only match (e.g., "en" matches "en-US", "ja" matches "ja-JP")
  const langPrefix = browserLang.split('-')[0].toLowerCase();
  const match = locales.find((l) => l.id.toLowerCase().startsWith(langPrefix));
  if (match) return match;

  return getLocale(DEFAULT_LOCALE_ID);
}

/**
 * Resolve display preferences using the fallback chain:
 *   view setting → profile default → locale auto-detection
 */
export function resolveDefaults(options?: {
  viewSetting?: { currencyCode?: CurrencyCode; weightUnit?: WeightUnit };
  profileDefault?: { currencyCode?: CurrencyCode; weightUnit?: WeightUnit };
}): { currencyCode: CurrencyCode; weightUnit: WeightUnit } {
  const detected = detectLocale();
  return {
    currencyCode: options?.viewSetting?.currencyCode ?? options?.profileDefault?.currencyCode ?? detected.currencyCode,
    weightUnit: options?.viewSetting?.weightUnit ?? options?.profileDefault?.weightUnit ?? detected.weightUnit,
  };
}
